import React, { useCallback, useMemo, useState } from "react";
import {
    TicketView as ApiTicketView, DashboardSettingsFilters, DashboardSortBy, TicketStatus, useGetTicketsQuery,
} from "../../__generated__/api";
import { Loading } from "../../components";
import useCreateTicket from "../../contexts/CreateTicketContext";
import useRouting from "../../contexts/Routing";
import TabList from "../../components/theme/tab-list/TabList";
import TopPanel, { WidgetType } from "../../components/agent-v3/top-panel/TopPanel";
import Styles from "./Tickets.module.scss";
import TicketsTable from "./TicketsTable";
import EmptyTickets from "./EmptyTickets";
import SortByFilter from "../../components/agent-v3/board-toolbar/SortBy";
import TicketView from "../../components/tickets-v3/TicketView";
import sortTickets from "./sortTickets";
import FiltersComponent from "../../components/agent-v3/board-toolbar/Filters";
import Filters from "../../contexts/ticket-state/filters";
import { LoggingHandler } from "../../contexts/logging/LoggingContext";

export type Ticket = Exclude<ReturnType<(typeof useGetTicketsQuery)>["data"], undefined>["tickets"][0];

const MENU_ITEMS = [
    {
        id: "open",
        title: "Open Tickets",
        canShowMenu: false,
    },
    {
        id: "closed",
        title: "Closed Tickets",
        canShowMenu: false,
    },
];

const DEFAULT_FILTERS = {
    createdDate: true,
    // conversationSources: true,
    priorities: true,
    // requestTypes: true,
};

export default function TicketsPage() {
    const {
        Paths, getSearchParams, historyPushSearch, createHistoryPush,
    } = useRouting();

    const { requestTypeId } = getSearchParams(Paths.TICKETS);
    const createTicketActions = useCreateTicket();

    const { data, loading: isLoading } = useGetTicketsQuery({
        variables: {
            filter: ApiTicketView.CreatedTickets,
            filters: {
                limit: 50,
            },
        },
    });

    const [selectedTab, setSelectedTab] = useState("open");
    const [sortBy, setSortBy] = useState<DashboardSortBy>(DashboardSortBy.Manual);
    const [filters, setFilters] = useState<DashboardSettingsFilters | undefined>(undefined);

    createTicketActions.onChangeRequestType(id => {
        if (id) {
            historyPushSearch({ requestTypeId: id });
        } else {
            historyPushSearch({});
        }
    });
    createTicketActions.onDone(() => {
        historyPushSearch({ });
    });

    const createTicket = useCreateTicket();

    const appliedFilters = useMemo(() => {
        if (!filters) {
            return [];
        }

        return Filters.getAll().filterMap(
            Filter => new Filter(filters),
            filter => filter.shouldCreate(filters),
        );
    }, [filters]);

    const filteredTickets = useMemo(
        () => data?.tickets.filter(ticket => appliedFilters.every(filter => filter.shouldIncludeTicket(ticket))),
        [data, appliedFilters],
    );
    const tickets = useMemo(() => sortTickets(filteredTickets ?? [], sortBy), [filteredTickets, sortBy]);

    const openTickets = useMemo(() => tickets?.filter(ticket => ticket.status !== TicketStatus.Ignored
        && ticket.status !== TicketStatus.Closed) ?? [], [tickets]);
    const closedTickets = useMemo(() => tickets?.filter(ticket => ticket.status !== TicketStatus.Ignored
        && ticket.status === TicketStatus.Closed) ?? [], [tickets]);

    if (requestTypeId) {
        createTicketActions.show(requestTypeId);
    }

    const createTicketRequest = useCallback(() => {
        createTicket.show();
    }, []);

    const openTicket = useCallback((ticketId: number | string) => {
        createHistoryPush(Paths.TICKETS_VIEW, { ticketId: String(ticketId) })();
    }, []);

    if (isLoading) {
        return <Loading />;
    }

    return (
        <div>
            <TopPanel
                title="My Tickets"
                activeWidgets={[
                    WidgetType.HELP,
                    WidgetType.CREATE_TICKET_REQUEST,
                ]}
                metricPathPrefix={[LoggingHandler.Metrics.TICKETS, "top-panel"]}
            />
            <div className={Styles["tickets-container"]}>
                <div className="d-flex justify-content-between align-items-center border-bottom">
                    <TabList
                        className="border-0"
                        items={MENU_ITEMS}
                        selectedId={selectedTab}
                        onSelected={selectedId => {
                            setSelectedTab(selectedId);
                        }}
                    />

                    <div className="d-flex gap-2">
                        <FiltersComponent
                            onChange={newFilters => {
                                setFilters(newFilters);
                            }}
                            value={filters}
                            types={DEFAULT_FILTERS}
                        />

                        <SortByFilter
                            onChange={newSort => {
                                setSortBy(newSort ?? DashboardSortBy.Manual);
                            }}
                            value={sortBy}
                        />
                    </div>
                </div>

                {
                    tickets.isEmpty ? (
                        <EmptyTickets createTicketRequest={createTicketRequest} />
                    ) : (
                        <>
                            <TicketsTable
                                tickets={selectedTab === "open" ? openTickets : closedTickets}
                                openTicket={openTicket}
                                createTicketRequest={createTicketRequest}
                            />
                            <TicketView />
                        </>
                    )
                }

            </div>

        </div>
    );
}
